import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type StatusTagVariant =
  | 'approved'
  | 'declined'
  | 'pending'
  | 'successful'
  | 'failed'
  | 'default'
  | 'lms-success'
  | 'lms-warning'
  | 'lms-error'
  | 'lms-info'
  | 'lms-neutral';

export interface StatusTagProps {
  variant: StatusTagVariant;
  children: React.ReactNode;
  className?: string;
}

const statusVariants = {
  approved: {
    background: 'var(--color-polar-green-1)',
    border: 'var(--color-polar-green-3)',
    text: 'var(--color-polar-green-6)',
    icon: 'check-circle',
    shape: 'pill',
  },
  successful: {
    background: 'var(--color-polar-green-1)',
    border: 'var(--color-polar-green-3)',
    text: 'var(--color-polar-green-6)',
    icon: 'check-circle',
    shape: 'pill',
  },
  declined: {
    background: 'var(--color-volcano-1)',
    border: 'var(--color-volcano-3)',
    text: 'var(--color-volcano-6)',
    icon: 'x-circle',
    shape: 'pill',
  },
  failed: {
    background: 'var(--color-volcano-1)',
    border: 'var(--color-volcano-3)',
    text: 'var(--color-volcano-6)',
    icon: 'x-circle',
    shape: 'pill',
  },
  pending: {
    background: 'var(--color-sunset-orange-1)',
    border: 'var(--color-sunset-orange-3)',
    text: 'var(--color-sunset-orange-6)',
    icon: 'clock',
    shape: 'pill',
  },
  default: {
    background: '#fafafa',
    border: '#d9d9d9',
    text: '#8c8c8c',
    icon: 'none',
    shape: 'pill',
  },
  /* Updated-LMS square tags (Figma 4653:25682 family) — style-named because
   * the same three looks serve future statuses (e.g. "No client found"). */
  'lms-success': {
    background: 'var(--color-lms-green-50)',
    border: 'var(--color-lms-green-200)',
    text: 'var(--color-lms-green-600)',
    icon: 'none',
    shape: 'tag',
  },
  'lms-warning': {
    background: 'var(--color-sunset-orange-1)',
    border: 'var(--color-sunset-orange-3)',
    text: 'var(--color-sunset-orange-6)',
    icon: 'none',
    shape: 'tag',
  },
  'lms-error': {
    background: 'var(--color-lms-red-50)',
    border: 'var(--color-lms-red-200)',
    text: 'var(--color-error-500)',
    icon: 'none',
    shape: 'tag',
  },
  /* Figma Status/Blue (e.g. a "Scheduled" campaign). */
  'lms-info': {
    background: 'var(--color-lms-blue-50)',
    border: 'var(--color-lms-blue-200)',
    text: 'var(--color-lms-blue-600)',
    icon: 'none',
    shape: 'tag',
  },
  'lms-neutral': {
    background: 'var(--color-neutral-n50)',
    border: 'var(--color-neutral-100)',
    text: 'var(--color-neutral-n400)',
    icon: 'none',
    shape: 'tag',
  },
} as const;

const renderIcon = (iconType: string, color: string) => {
  switch (iconType) {
    case 'check-circle':
      return (
        <svg
          className="relative z-10"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6" cy="6" r="5.5" stroke={color} strokeWidth="1" />
          <path
            d="M3.5 6L5 7.5L8.5 4"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'x-circle':
      return (
        <svg
          className="relative z-10"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6" cy="6" r="5.5" stroke={color} strokeWidth="1" />
          <path
            d="M4 4L8 8M8 4L4 8"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'clock':
      return (
        <svg
          className="relative z-10"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6" cy="6" r="5.5" stroke={color} strokeWidth="1" />
          <path
            d="M6 3V6L8 7"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

export const StatusTag = forwardRef<HTMLDivElement, StatusTagProps>(
  ({ variant, children, className }, ref) => {
    const styles = statusVariants[variant];
    const isTag = styles.shape === 'tag';

    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 relative',
          isTag
            ? 'h-7 justify-center px-3 py-1.5 rounded-[6px]'
            : 'px-2 py-0.5 rounded-[22px]',
          className
        )}
        style={{
          backgroundColor: styles.background,
        }}
      >
        <div
          className="absolute border border-solid inset-0 pointer-events-none rounded-[inherit]"
          style={{ borderColor: styles.border }}
          aria-hidden="true"
        />
        {renderIcon(styles.icon, styles.text)}
        <span
          className={cn(
            'font-roboto text-[12px] relative z-10',
            isTag ? 'font-medium leading-4' : 'font-normal leading-[20px]'
          )}
          style={{ color: styles.text }}
        >
          {children}
        </span>
      </div>
    );
  }
);

StatusTag.displayName = 'StatusTag';
